import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import './App.css';

// Retained utility pages
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import Admin from './pages/Admin';
import NotFound from './pages/NotFound';
import TrainingTerms from './pages/TrainingTerms';
import Podcasts from './pages/Podcasts';

// Training Members Portal (PWA)
import PortalLayout from './pages/portal/PortalLayout';
import PortalLogin from './pages/portal/PortalLogin';
import PortalDashboard from './pages/portal/PortalDashboard';
import PortalModules from './pages/portal/PortalModules';
import PortalModule from './pages/portal/PortalModule';
import PortalJournal from './pages/portal/PortalJournal';
import PortalProfile from './pages/portal/PortalProfile';
import { PortalCohort, PortalCohortMember } from './pages/portal/PortalCohort';
import PortalCommunity, { PortalCommunityChannel, PortalCommunityPost } from './pages/portal/PortalCommunity';
import PortalCertificate from './pages/portal/PortalCertificate';
import PortalAdmin from './pages/portal/PortalAdmin';
import PortalShell from './pages/portal/PortalShell';

// v2 — Pro Plan rebuild (Feb 2026)
import V2Header from './components/v2/V2Header';
import V2Footer from './components/v2/V2Footer';
import TickerTape from './components/TickerTape';
import PodcastProduction from './pages/v2/PodcastProduction';
import PodcastEditing from './pages/v2/PodcastEditing';
import PodcastLaunch from './pages/v2/PodcastLaunch';
import PodcastToYoutube from './pages/v2/PodcastToYoutube';
import AudiobookProduction from './pages/v2/AudiobookProduction';
import SelfPublishing from './pages/v2/SelfPublishing';
import Seo from './pages/v2/Seo';
import BuildYourPackage from './pages/v2/BuildYourPackage';
import ProHome from './pages/v2/ProHome';
import ProPlan from './pages/v2/ProPlan';
import AuthorityHub from './pages/v2/AuthorityHub';
import CaseStudy from './pages/v2/CaseStudy';
import CaseStudiesIndex from './pages/v2/CaseStudiesIndex';
import Results from './pages/v2/Results';
import WebsitesApps from './pages/v2/WebsitesApps';
import ContactV2 from './pages/v2/ContactV2';
import BookCall from './pages/v2/BookCall';
import Booked from './pages/v2/Booked';
import About from './pages/v2/About';
import TrainingV2 from './pages/v2/TrainingV2';
import Pillar from './pages/v2/Pillar';
import PodcastManagement from './pages/v2/PodcastManagement';
import GuidesIndex from './pages/v2/GuidesIndex';

// Components
import ScrollToTop from './components/ScrollToTop';
import MarketingChrome from './components/MarketingChrome';
import VisitorTracker from './components/VisitorTracker';
import BiliChat from './components/v2/BiliChat';
import MobileStickyCTA from './components/v2/MobileStickyCTA';

// Global SEO Schema Components (site-wide, for AI Search)
import OrganizationSchema from './components/OrganizationSchema';
import LocalBusinessSchema from './components/LocalBusinessSchema';
import BreadcrumbSchema from './components/BreadcrumbSchema';
import WebSiteSchema from './components/WebSiteSchema';
import PersonSchema from './components/PersonSchema';
import AIAssistantSchema from './components/AIAssistantSchema';
import SpeakableSchema from './components/SpeakableSchema';

function App() {
  return (
    <div className="App min-h-screen bg-white">
      <Router>
        <ScrollToTop />
        <VisitorTracker />
        {/* Global SEO Schemas — applied site-wide for AI Search */}
        <OrganizationSchema />
        <LocalBusinessSchema />
        <BreadcrumbSchema />
        <WebSiteSchema />
        <PersonSchema />
        <AIAssistantSchema />
        <SpeakableSchema
          name="Audio & Co. — Done-for-you Podcast & Content Repurposing"
          description="Weekly done-for-you podcast production and content repurposing service for small business owners. £280/week Pro Plan. Fully remote, working with clients worldwide."
          speakableSections={['h1', 'h2', '.hero-description', '[data-speakable]', '.faq-answer']}
        />

        <MarketingChrome>
          <TickerTape />
          <V2Header />
        </MarketingChrome>
        <main>
          <Routes>
            {/* v2 — Pro Plan public site */}
            <Route path="/" element={<ProHome />} />
            {/* Package page — new URL (Podcast Visibility Package) with 301
                redirect from the legacy /pro-plan slug per Sep 2026 rebuild spec. */}
            <Route path="/podcast-visibility-package" element={<ProPlan />} />
            <Route path="/pro-plan" element={<Navigate to="/podcast-visibility-package" replace />} />
            {/* NEW Sep 2026 service landing pages */}
            <Route path="/podcast-production" element={<PodcastProduction />} />
            <Route path="/podcast-editing" element={<PodcastEditing />} />
            <Route path="/podcast-launch" element={<PodcastLaunch />} />
            <Route path="/podcast-to-youtube" element={<PodcastToYoutube />} />
            <Route path="/audiobook-production" element={<AudiobookProduction />} />
            <Route path="/self-publishing" element={<SelfPublishing />} />
            <Route path="/seo" element={<Seo />} />
            <Route path="/build-your-package" element={<BuildYourPackage />} />
            <Route path="/authority-hub" element={<AuthorityHub />} />
            <Route path="/results" element={<Results />} />
            <Route path="/case-studies" element={<CaseStudiesIndex />} />
            <Route path="/case-studies/:slug" element={<CaseStudy />} />
            <Route path="/websites-apps" element={<WebsitesApps />} />
            <Route path="/contact" element={<ContactV2 />} />
            <Route path="/book" element={<BookCall />} />
            <Route path="/booked" element={<Booked />} />
            <Route path="/about" element={<About />} />
            <Route path="/training" element={<TrainingV2 />} />
            <Route path="/training-terms" element={<TrainingTerms />} />

            {/* SEO topic hubs (pillar pages) */}
            <Route path="/guides" element={<GuidesIndex />} />
            <Route path="/guides/:slug" element={<Pillar />} />
            <Route path="/podcast-launching" element={<Navigate to="/podcast-launch" replace />} />
            <Route path="/book-publishing" element={<Pillar pillarSlug="book-publishing" />} />

            {/* SEO service page: podcast management */}
            <Route path="/podcast-management" element={<PodcastManagement />} />

            {/* Blog (v2 restyled) */}
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />

            {/* Training Members Portal (PWA) — own auth + chrome */}
            <Route path="/portal/login" element={<PortalShell><PortalLogin /></PortalShell>} />
            <Route path="/portal" element={<PortalShell><PortalLayout /></PortalShell>}>
              <Route index element={<PortalDashboard />} />
              <Route path="modules" element={<PortalModules />} />
              <Route path="modules/:moduleId" element={<PortalModule />} />
              <Route path="journal" element={<PortalJournal />} />
              <Route path="cohort" element={<PortalCohort />} />
              <Route path="cohort/:userId" element={<PortalCohortMember />} />
              <Route path="community" element={<PortalCommunity />} />
              <Route path="community/post/:postId" element={<PortalCommunityPost />} />
              <Route path="community/:channelSlug" element={<PortalCommunityChannel />} />
              <Route path="profile" element={<PortalProfile />} />
              <Route path="certificate" element={<PortalCertificate />} />
              <Route path="admin" element={<PortalAdmin />} />
            </Route>

            {/* Utility pages */}
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/command-center" element={<Navigate to="/admin" replace />} />

            {/* 301 redirects — retired v1 URLs → v2 equivalents */}
            <Route path="/home" element={<Navigate to="/" replace />} />
            <Route path="/packages" element={<Navigate to="/pro-plan" replace />} />
            <Route path="/pricing" element={<Navigate to="/pro-plan" replace />} />
            <Route path="/services" element={<Navigate to="/pro-plan" replace />} />
            <Route path="/services/*" element={<Navigate to="/pro-plan" replace />} />
            <Route path="/podcasts" element={<Podcasts />} />
            <Route path="/podcasting" element={<Navigate to="/pro-plan" replace />} />
            <Route path="/audiobooks" element={<Navigate to="/pro-plan" replace />} />
            <Route path="/youtube" element={<Navigate to="/pro-plan" replace />} />
            <Route path="/why-different" element={<Navigate to="/pro-plan" replace />} />
            <Route path="/your-journey" element={<Navigate to="/pro-plan" replace />} />
            <Route path="/faq" element={<Navigate to="/pro-plan" replace />} />
            <Route path="/about-jo" element={<Navigate to="/about" replace />} />
            <Route path="/website-mobile" element={<Navigate to="/websites-apps" replace />} />
            <Route path="/free-seo-audit" element={<Navigate to="/blog" replace />} />
            <Route path="/audit/:auditId" element={<Navigate to="/blog" replace />} />
            <Route path="/seo-visibility-guide" element={<Navigate to="/blog" replace />} />
            <Route path="/book-a-call" element={<Navigate to="/book" replace />} />
            <Route path="/free-consultation" element={<Navigate to="/book" replace />} />

            {/* Old blog slug cleanups */}
            <Route
              path="/blog/youtube-algorithm-secrets-get-more-views-2024"
              element={<Navigate to="/blog/youtube-algorithm-secrets-get-more-views" replace />}
            />

            {/* Old WP taxonomy / share URLs */}
            <Route path="/category/*" element={<Navigate to="/blog" replace />} />
            <Route path="/tag/*" element={<Navigate to="/blog" replace />} />

            <Route path="*" element={<NotFound />} />
          </Routes>
        </main>
        <MarketingChrome>
          <V2Footer />
        </MarketingChrome>
        <MarketingChrome>
          <BiliChat />
        </MarketingChrome>
        <MarketingChrome>
          <MobileStickyCTA />
        </MarketingChrome>
        <Toaster />
      </Router>
    </div>
  );
}

export default App;
